import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Unipile from "@/services/unipile/unipile";
import type { GetChatMessagesRequest } from "@/dtos/Unipile.dto";

export interface LinkedInConversationMessagesParams {
  conversationId: string;
  before?: string | null;
  after?: string | null;
  limit?: number;
  cursor?: string;
  sender_id?: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useLinkedInConversationMessages(
  params: Ref<LinkedInConversationMessagesParams | null> | LinkedInConversationMessagesParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["linkedin-messages", "disabled"];
    return [
      "linkedin-messages",
      p.conversationId,
      p.before || "",
      p.after || "",
      p.limit || 10,
      p.cursor || "",
    ];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.conversationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const requestParams: GetChatMessagesRequest = {};
      if (p.before) requestParams.before = p.before;
      if (p.after) requestParams.after = p.after;
      if (p.limit) requestParams.limit = p.limit;
      if (p.cursor) requestParams.cursor = p.cursor;
      if (p.sender_id) requestParams.sender_id = p.sender_id;

      const response = await Unipile.getConversationMessages(p.conversationId, requestParams);

      if (response.data?.status) {
        return response.data.data;
      }

      throw new Error("Failed to load LinkedIn conversation messages");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
